/**
 * ResendInvoiceDialog
 *
 * Feature 7 — Resend Invoice (PRD: BILLING_PHASE2_PRD.md)
 *
 * Opens as a Dialog when the user clicks "Resend" from the ... menu on a
 * Sent, Overdue, or Partially Paid invoice.
 *
 * Behaviour:
 *  1. Pre-fills the email form using the default "resend" type template
 *     from DEFAULT_EMAIL_TEMPLATES (the "Standard Resend Email").
 *  2. All fields (To, CC, Subject, Body) are fully editable before sending.
 *  3. Clicking "Send Now" navigates to the Platform Emailer holding page
 *     (same UX pattern as SendInvoiceStep — intentional).
 *  4. Confirming the send records a mock audit entry (SendHistoryEntry) that
 *     is returned to the parent so InvoiceViewerOverlay can display it.
 *
 * Mock data strategy
 * ──────────────────
 * All email content is populated from DEFAULT_EMAIL_TEMPLATES['resend'] seed
 * data with simple token substitution. The send audit trail is stored only in
 * React component state — it is NOT persisted to a backend.
 *
 * TODO (Engineering — Feature 7):
 *   Replace handlePlatformEmailerConfirm with:
 *     POST /api/invoices/{invoiceId}/resend
 *     Body: { to, cc, subject, body }
 *   On success the server should record the send event and return the updated
 *   send history, which replaces the local `SendHistoryEntry` mock entries.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
// Card/CardContent removed — no longer used after replacing holding page with MFE
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Send,
  Paperclip,
  X,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { DEFAULT_EMAIL_TEMPLATES } from './EmailTemplateEditor';
import type { EmailTemplate, EmailTemplateType } from './EmailTemplateEditor';
import { EmailAddressInput } from './EmailAddressInput';
import { fetchEmailTemplates } from '@/services/emailTemplateService';
import type { ApiEmailTemplate } from '@/services/emailTemplateService';
import { resolveTokensInText, resolveTokensInHtml } from '@/services/mergeTokens';
import { resolveRecipients } from './resend-invoice-recipients';
import { useDataMode } from '@/contexts/DataModeContext';
import { useAuth } from '@/providers/AuthProvider';
import { openMfe, closeMfe } from '@access-evo/evo-api';
import { getStoredInvoicePdf, blobToBase64 } from '@/services/invoice-pdf-service';
import {
  SENDIT_MFE_NAME,
  isSendItCallbackDetail,
  type SendItCallbackDetail,
  type SendItEmailPayload,
  type SendItMfeOptions,
} from '@/contracts/sendit-mfe';
import { logMfeFailure } from '@/services/mfe-failure-logger';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A single entry in the send/resend audit trail for an invoice.
 *
 * TODO (Engineering): This type should mirror the response shape of
 *   GET /api/invoices/:id/send-history
 *   Each entry is created server-side when POST /api/invoices/:id/resend succeeds.
 */
export interface SendHistoryEntry {
  /** Unique entry ID — use server-generated UUID in production */
  id: string;
  /** ISO 8601 timestamp of when the send/resend was triggered */
  sentAt: string;
  /** Display name of the user who triggered the send */
  sentByName: string;
  /** Primary recipient email addresses */
  toEmail: string[];
  /** CC recipients (may be empty) */
  ccEmail?: string[];
  /** BCC recipients (may be empty) */
  bccEmail?: string[];
  /** Email subject line */
  subject: string;
  /** Whether this was the original send (false) or a resend (true) */
  isResend: boolean;
}

export interface ResendInvoiceDialogProps {
  /** Controls dialog open/closed state */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Invoice number string, e.g. "INV-2024-042" */
  invoiceNumber: string;
  /** Invoice ID (for the future API call) */
  invoiceId?: number;
  /** Pre-fill To field — client's billing email */
  clientEmail?: string;
  /** Client contact first name (for token resolution) */
  contactFirstName?: string;
  /** Client name (for token resolution) */
  clientName?: string;
  /** Client phone number (for token resolution) */
  clientPhone?: string;
  /** Client account number / code (for token resolution) */
  clientAccountNumber?: string;
  /** Client payment terms in days (for token resolution) */
  clientPaymentTermsDays?: string;
  /** Project name (for token resolution) */
  projectName?: string;
  /** Project code (for token resolution) */
  projectCode?: string;
  /** Invoice total formatted, e.g. "£12,450.00" */
  invoiceTotal?: string;
  /** Invoice due date formatted */
  dueDate?: string;
  /** Company name (for token resolution) */
  companyName?: string;
  /** Sender name (for token resolution) */
  senderName?: string;
  /** Sender email (for token resolution) */
  senderEmail?: string;
  /**
   * Called when a resend is successfully confirmed.
   * The parent pushes the entry to the project-level Audit tab.
   */
  onResent?: (entry: SendHistoryEntry) => void;
  /**
   * Base64-encoded invoice PDF to attach to the resend email.
   * When provided, the PDF is included in the bulkPayload attachments
   * so the Evo Emailer receives the file.
   */
  invoicePdfBase64?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Token resolution
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Replaces merge tokens in a string with real (or mock) values, using the
 * shared resolver so behaviour matches every other screen.
 */
function resolveTokens(text: string, data: Record<string, string>): string {
  return resolveTokensInText(text, data);
}

/** Strip MergeFieldEditor chip HTML and replace with plain text values */
function resolveHtmlToPlainText(html: string, data: Record<string, string>): string {
  if (!html) return '';

  // 1. Resolve chip spans + raw tokens via the shared resolver
  let text = resolveTokensInHtml(html, data);

  // 2. Block element endings → newlines
  text = text.replace(/<\/p>/gi, '\n');
  text = text.replace(/<br\s*\/?>/gi, '\n');

  // 3. Strip remaining tags
  text = text.replace(/<[^>]+>/g, '');

  // 4. Decode HTML entities
  text = text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"');

  // 5. Normalise whitespace — collapse 3+ newlines to 2
  text = text.replace(/\n{3,}/g, '\n\n').trim();

  return text;
}


// Send history is shown in the project Audit tab, not in this dialog.
// See: pages/project-details/tabs/audit-tab.tsx

// ─────────────────────────────────────────────────────────────────────────────
// Main dialog
// ─────────────────────────────────────────────────────────────────────────────

function mapApiToFrontend(api: ApiEmailTemplate): EmailTemplate {
  let parsedVars: { to?: string[]; cc?: string[]; bcc?: string[] } = {};
  if (api.variables) {
    try { parsedVars = JSON.parse(api.variables); } catch { /* ignore */ }
  }
  return {
    id: String(api.id),
    name: api.name,
    type: (api.category || 'invoice') as EmailTemplateType,
    to: parsedVars.to ?? [],
    cc: parsedVars.cc ?? [],
    bcc: parsedVars.bcc ?? [],
    subject: api.subject,
    body: '',
    bodyHtml: api.body,
    isDefault: api.isDefault,
    createdAt: new Date(api.createdAt),
    updatedAt: new Date(api.updatedAt),
  };
}

export function ResendInvoiceDialog({
  open,
  onOpenChange,
  invoiceNumber,
  invoiceId,
  clientEmail = '',
  contactFirstName = '',
  clientName = '',
  clientPhone = '',
  clientAccountNumber = '',
  clientPaymentTermsDays = '',
  projectName = '',
  projectCode = '',
  invoiceTotal = '',
  dueDate = '',
  companyName = '',
  senderName = '',
  senderEmail = '',
  onResent,
  invoicePdfBase64,
}: ResendInvoiceDialogProps) {
  const { isDemoMode } = useDataMode();
  const { instanceId } = useAuth();
  const [mfeOpen, setMfeOpen] = useState(false);
  // ── Template state ──────────────────────────────────────────────────────
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>(DEFAULT_EMAIL_TEMPLATES);

  useEffect(() => {
    if (isDemoMode) {
      setEmailTemplates(DEFAULT_EMAIL_TEMPLATES);
      return;
    }
    let cancelled = false;
    fetchEmailTemplates()
      .then((apiTemplates) => {
        if (!cancelled) {
          setEmailTemplates(apiTemplates.map(mapApiToFrontend));
        }
      })
      .catch((err) => {
        console.error('Failed to load email templates:', err);
        if (!cancelled) {
          setEmailTemplates(DEFAULT_EMAIL_TEMPLATES);
        }
      });
    return () => { cancelled = true; };
  }, [isDemoMode]);

  const defaultResendTemplate =
    emailTemplates.find((t) => t.type === 'resend' && t.isDefault) ??
    emailTemplates.find((t) => t.type === 'resend') ??
    emailTemplates[0];

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(
    defaultResendTemplate?.id ?? '',
  );

  // ── Form fields ─────────────────────────────────────────────────────────
  const [to, setTo]   = useState<string[]>(clientEmail ? [clientEmail] : []);
  const [cc, setCc]   = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  // ── UI state ─────────────────────────────────────────────────────────────
  const [isSending, setIsSending] = useState(false);

  // ── PDF attachment — fetch stored PDF when dialog opens ─────────────────
  const [resolvedPdfBase64, setResolvedPdfBase64] = useState<string | undefined>(invoicePdfBase64);

  useEffect(() => {
    if (!open) return;
    // If already provided as a prop, use that
    if (invoicePdfBase64) {
      setResolvedPdfBase64(invoicePdfBase64);
      return;
    }
    // Fetch the stored PDF using the numeric invoice ID (consistent with rest of codebase)
    const pdfKey = invoiceId != null ? String(invoiceId) : invoiceNumber;
    if (!pdfKey) return;
    let cancelled = false;
    getStoredInvoicePdf(pdfKey)
      .then(async (stored) => {
        if (cancelled || !stored) return;
        const b64 = await blobToBase64(stored.pdfBlob);
        if (!cancelled) setResolvedPdfBase64(b64);
      })
      .catch(() => { /* PDF fetch failed — send without attachment */ });
    return () => { cancelled = true; };
  }, [open, invoiceNumber, invoiceId, invoicePdfBase64]);

  // Token map for merge field resolution.
  // Memoised so the template-resolution effect re-runs only when a value changes.
  const tokenData: Record<string, string> = useMemo(() => ({
    '{client.name}':             clientName,
    '{client.contactName}':      contactFirstName,
    '{client.contactFirstName}': contactFirstName,
    '{client.email}':            clientEmail,
    '{client.phone}':            clientPhone,
    '{client.accountNumber}':    clientAccountNumber,
    '{client.paymentTermsDays}': clientPaymentTermsDays,
    '{invoice.number}':          invoiceNumber,
    '{invoice.date}':            new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
    '{invoice.dueDate}':         dueDate,
    '{invoice.total}':           invoiceTotal,
    '{invoice.amountDue}':       invoiceTotal,
    '{project.name}':            projectName,
    '{project.code}':            projectCode,
    '{company.name}':            companyName,
    '{company.senderName}':      senderName,
    '{company.senderEmail}':     senderEmail,
  }), [
    clientName, contactFirstName, clientEmail, clientPhone,
    clientAccountNumber, clientPaymentTermsDays,
    invoiceNumber, dueDate, invoiceTotal,
    projectName, projectCode, companyName, senderName, senderEmail,
  ]);

  // Re-select the default template when the template list changes (e.g. after
  // the API response replaces mock data with real templates whose IDs differ).
  useEffect(() => {
    const best =
      emailTemplates.find((t) => t.type === 'resend' && t.isDefault) ??
      emailTemplates.find((t) => t.type === 'resend') ??
      emailTemplates[0];
    if (best) setSelectedTemplateId(best.id);
  }, [emailTemplates]);

  // Reset form when dialog opens or invoice changes
  useEffect(() => {
    if (open) {
      setTo(clientEmail ? [clientEmail] : []);
      setCc([]);
      setSelectedTemplateId(defaultResendTemplate?.id ?? '');
      setIsSending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoiceNumber]);

  useEffect(() => {
    const tpl = emailTemplates.find((t) => t.id === selectedTemplateId);
    if (tpl) {
      const resolvedTo = resolveRecipients(tpl.to, tokenData);
      setTo(resolvedTo.length > 0 ? resolvedTo : (clientEmail ? [clientEmail] : []));
      setCc(resolveRecipients(tpl.cc, tokenData));
      setSubject(resolveTokens(tpl.subject, tokenData));
      // Convert the rich bodyHtml into readable plain text for the textarea
      setBody(resolveHtmlToPlainText(tpl.bodyHtml, tokenData));
    }
  }, [selectedTemplateId, emailTemplates, tokenData, clientEmail]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const CALLBACK_EVENT = 'projects:sendit-resend-done';
  const mfePollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clean up MFE poll on unmount
  useEffect(() => {
    return () => {
      if (mfePollRef.current) clearInterval(mfePollRef.current);
    };
  }, []);

  const handleSendNow = useCallback(async () => {
    setIsSending(true);

    const emailAttachments: { type: 'base64'; filename: string; mimeType: string; data: string }[] = [];
    if (resolvedPdfBase64) {
      emailAttachments.push({
        type: 'base64',
        filename: `${invoiceNumber}.pdf`,
        mimeType: 'application/pdf',
        data: resolvedPdfBase64,
      });
    }

    const singleEmailPayload: SendItEmailPayload = {
      to,
      cc,
      subject,
      body,
      attachments: emailAttachments,
    };

    let callbackReceived = false;
    let sentSuccessfully = false;
    let resendEntry: SendHistoryEntry | undefined;
    let mfeInstanceId: string | undefined;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;

      if (!isSendItCallbackDetail(detail)) {
        console.warn('[SendIt MFE] Unexpected callback detail shape:', detail);
        logMfeFailure({
          mfeName: SENDIT_MFE_NAME,
          errorMessage: `Unexpected callback detail shape: ${JSON.stringify(detail)}`,
          errorType: 'unknown',
          component: 'ResendInvoiceDialog',
          timestamp: new Date().toISOString(),
          invoiceNumber,
        });
      }

      const typedDetail = detail as SendItCallbackDetail;

      // MFE Cancel / Close / Dismiss button — clean up and close dialog.
      // After a successful send the MFE shows a success screen whose
      // "Close" button may fire with a different action value (e.g.
      // 'dismiss') or no action at all — so once sentSuccessfully is true
      // we treat any subsequent callback as the user dismissing the MFE.
      const isCloseAction =
        typedDetail?.action === 'close' ||
        typedDetail?.action === 'dismiss' ||
        typedDetail?.action === 'cancel' ||
        sentSuccessfully;

      if (isCloseAction) {
        window.removeEventListener(CALLBACK_EVENT, handler);
        callbackReceived = true;
        if (mfePollRef.current) {
          clearInterval(mfePollRef.current);
          mfePollRef.current = null;
        }
        if (mfeInstanceId) {
          closeMfe(mfeInstanceId).catch(() => {});
        }
        setMfeOpen(false);
        if (sentSuccessfully && resendEntry) {
          onResent?.(resendEntry);
        }
        onOpenChange(false);
        return;
      }

      // MFE success — close the MFE immediately so the user does not
      // see the intermediate "Email sent successfully" screen.
      sentSuccessfully = true;
      callbackReceived = true;
      resendEntry = {
        id: `resend-${Date.now()}`,
        sentAt: new Date().toISOString(),
        sentByName: senderName,
        toEmail: to,
        ccEmail: cc.length > 0 ? cc : undefined,
        subject,
        isResend: true,
      };
      window.removeEventListener(CALLBACK_EVENT, handler);
      if (mfePollRef.current) {
        clearInterval(mfePollRef.current);
        mfePollRef.current = null;
      }
      if (mfeInstanceId) {
        closeMfe(mfeInstanceId).catch(() => {});
      }
      setMfeOpen(false);
      toast.success(`Invoice ${invoiceNumber} resent successfully.`, {
        description: `Email delivered to ${to.join(', ')}`,
      });
      onResent?.(resendEntry);
      onOpenChange(false);
    };
    window.addEventListener(CALLBACK_EVENT, handler);

    setMfeOpen(true);

    try {
      const mfeOptions: SendItMfeOptions = {
        instanceId: instanceId ?? undefined,
        parameters: {
          mode: 'single',
          email: JSON.stringify(singleEmailPayload),
          callbackEventName: CALLBACK_EVENT,
          autoCloseOnSuccess: 'true',
        },
        type: 'modal',
      };
      const result = await openMfe(SENDIT_MFE_NAME, mfeOptions);
      mfeInstanceId = result.instanceId;
    } catch (err: unknown) {
      const isCloneError =
        err instanceof Error && err.message.includes('could not be cloned');
      if (!isCloneError) {
        window.removeEventListener(CALLBACK_EVENT, handler);
        toast.error('Failed to open SendIt. Please try again.');
        logMfeFailure({
          mfeName: SENDIT_MFE_NAME,
          errorMessage: err instanceof Error ? err.message : String(err),
          errorType: 'open_failed',
          component: 'ResendInvoiceDialog',
          timestamp: new Date().toISOString(),
          invoiceNumber,
          instanceId: instanceId ?? undefined,
        });
        setIsSending(false);
        setMfeOpen(false);
        return;
      }
      // Clone error — MFE opened but the return value could not be
      // serialised.  Recover the instance ID from the platform registry
      // so the dismissal poller can still track it.
      try {
        const instances = window.AccessEvo?.mfes?.instances;
        if (instances) {
          const keys = Object.keys(instances);
          if (keys.length > 0) mfeInstanceId = keys[keys.length - 1];
        }
      } catch { /* registry unavailable — polling will use fallback */ }
    }

    // Poll for MFE dismissal (Cancel, X, or Close button).
    // Runs regardless of whether openMfe threw a clone error so we
    // always detect when the emailer is closed.
    if (mfePollRef.current) clearInterval(mfePollRef.current);
    mfePollRef.current = setInterval(() => {
      const instances = window.AccessEvo?.mfes?.instances;

      if (mfeInstanceId) {
        const instanceGone = !instances || !(mfeInstanceId! in instances);
        const instance = instances?.[mfeInstanceId!];
        const containerDetached =
          instance?.container && !document.body.contains(instance.container);

        if (instanceGone || containerDetached) {
          if (mfePollRef.current) clearInterval(mfePollRef.current);
          mfePollRef.current = null;
          if (!callbackReceived) {
            window.removeEventListener(CALLBACK_EVENT, handler);
          }
          setMfeOpen(false);
          if (sentSuccessfully && resendEntry) {
            onResent?.(resendEntry);
          }
          onOpenChange(false);
        }
      } else {
        // No instance ID (clone error + registry lookup failed).
        // Fall back to checking whether every MFE instance has been removed.
        const noInstancesLeft = !instances || Object.keys(instances).length === 0;
        if (noInstancesLeft) {
          if (mfePollRef.current) clearInterval(mfePollRef.current);
          mfePollRef.current = null;
          if (!callbackReceived) {
            window.removeEventListener(CALLBACK_EVENT, handler);
          }
          setMfeOpen(false);
          if (sentSuccessfully && resendEntry) {
            onResent?.(resendEntry);
          }
          onOpenChange(false);
        }
      }
    }, 300);

    setIsSending(false);
  }, [to, cc, subject, body, invoiceNumber, senderName, instanceId, onResent, onOpenChange, resolvedPdfBase64]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={!mfeOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Resend Invoice — {invoiceNumber}
          </DialogTitle>
          <DialogDescription>
            Review and edit the email before resending to the client.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-1">
            {/* ── Template selector ──────────────────────────────────────── */}
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                Email Template
              </Label>
              <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a template…" />
                </SelectTrigger>
                <SelectContent>
                  {emailTemplates.map((tpl: EmailTemplate) => (
                    <SelectItem key={tpl.id} value={tpl.id}>
                      <div className="flex items-center gap-2">
                        <span>{tpl.name}</span>
                        {tpl.isDefault && tpl.type === 'resend' && (
                          <Badge variant="secondary" className="text-xs py-0">Default</Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Separator />

            {/* ── To / CC ────────────────────────────────────────────────── */}
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="resend-to">To</Label>
                <EmailAddressInput
                  id="resend-to"
                  value={to}
                  onChange={setTo}
                  placeholder="client@example.com"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="resend-cc">
                  CC <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <EmailAddressInput
                  id="resend-cc"
                  value={cc}
                  onChange={setCc}
                  placeholder="cc@example.com"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Press <kbd className="bg-muted border rounded px-1 py-0.5 text-[11px]">Enter</kbd> or <kbd className="bg-muted border rounded px-1 py-0.5 text-[11px]">,</kbd> after each address to add it.
              </p>
            </div>

            {/* ── Subject ────────────────────────────────────────────────── */}
            <div className="space-y-1.5">
              <Label htmlFor="resend-subject">Subject</Label>
              <Input
                id="resend-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            {/* ── Body ───────────────────────────────────────────────────── */}
            <div className="space-y-1.5">
              <Label htmlFor="resend-body">Message</Label>
              <Textarea
                id="resend-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="min-h-[180px] font-mono text-xs resize-none"
              />
            </div>

            {/* ── Attachment indicator ───────────────────────────────────── */}
            {/* TODO (Engineering): Replace with real attachment from the
                document management store once GET /billing/invoices/:id/pdf
                is available. */}
            <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
              <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-sm text-muted-foreground">Attachment:</span>
              <Badge variant="outline" className="text-xs font-mono">
                {invoiceNumber}.pdf
              </Badge>
              <span className="text-xs text-muted-foreground ml-auto">
                {/* MOCK: real file size from the document store */}
                ~245 KB
              </span>
            </div>

            {/* ── Actions ───────────────────────────────────────────────── */}
            <div className="flex items-center justify-between pt-1">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleSendNow}
                disabled={to.length === 0 || !subject.trim() || isSending}
                size="sm"
              >
                <Send className="h-4 w-4 mr-2" />
                {isSending ? 'Opening…' : 'Send Now'}
              </Button>
            </div>
          </div>
      </DialogContent>
    </Dialog>
  );
}
